import React, { useState } from 'react';
import axios from 'axios';
import { SalesStatus, APIs } from '../../utils/Constants';
import { Routes } from '../../utils/Routes';

type PricingInfoProps = {
  metadata: {
    id: string;
    title: string;
    status: string;
    price?: {
      amount: number;
      currency: string;
    };
  };
  basePath?: string;
};

const InquiryForm = ({
  artTitle,
  artId,
  basePath = Routes.Art,
}: {
  artTitle: string;
  artId: string;
  basePath?: string;
}) => {
  const [status, setStatus] = useState<'idle' | 'open' | 'success' | 'error'>(
    'idle',
  );
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const artLink = `${window.location.origin}${basePath}/${artId}`;
      await axios.post(
        process.env.REACT_APP_UNSPOKEN_SERVICES_API + APIs.paintingInquiry,
        { ...form, paintingTitle: artTitle, paintingLink: artLink },
      );
      setStatus('success');
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full border-b border-gray-300 bg-transparent text-sm text-gray-800 py-2 placeholder-gray-400 focus:outline-none focus:border-custom-coffee transition-colors duration-200';

  if (status === 'success') {
    return (
      <div className="border-l-2 border-custom-olive pl-4 py-1 space-y-3">
        <div className="flex items-center gap-2">
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-custom-olive shrink-0"
          >
            <path
              d="M3 8l4 4 6-7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-sm text-gray-700">
            Thank you, <span className="font-medium">{form.name}</span>.
          </p>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          Your inquiry has been received. We'll be in touch soon.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="text-[11px] uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors duration-200"
        >
          Close
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="border-l-2 border-red-400 pl-4 py-1 space-y-3">
        <div className="flex items-center gap-2">
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-red-400 shrink-0"
          >
            <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
          </svg>
          <p className="text-sm text-gray-700">Something went wrong.</p>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          Your inquiry couldn't be sent. Please try again or reach out directly.
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setStatus('open')}
            className="text-[11px] uppercase tracking-widest text-custom-coffee hover:underline transition-colors duration-200"
          >
            Try again
          </button>
          <button
            onClick={() => setStatus('idle')}
            className="text-[11px] uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {status === 'idle' ? (
        <button
          onClick={() => setStatus('open')}
          className="text-xs uppercase tracking-widest text-custom-coffee border border-custom-coffee px-4 py-2 hover:bg-custom-coffee hover:text-white transition-colors duration-200"
        >
          Inquire About This Piece
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <p className="text-[11px] uppercase tracking-widest text-gray-400 mb-4">
            Inquire about &ldquo;{artTitle}&rdquo;
          </p>

          <input
            name="name"
            type="text"
            placeholder="Your name"
            required
            value={form.name}
            onChange={handleChange}
            className={inputClass}
          />
          <input
            name="email"
            type="email"
            placeholder="Email address"
            required
            value={form.email}
            onChange={handleChange}
            className={inputClass}
          />
          <textarea
            name="message"
            placeholder="Message (optional)"
            rows={3}
            value={form.message}
            onChange={handleChange}
            className={`${inputClass} resize-none`}
          />

          <div className="flex gap-4 pt-1">
            <button
              type="submit"
              disabled={isSubmitting}
              className="text-xs uppercase tracking-widest text-white bg-custom-coffee px-4 py-2 hover:bg-opacity-80 transition-colors duration-200 disabled:opacity-50"
            >
              {isSubmitting ? 'Sending…' : 'Send Inquiry'}
            </button>
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="text-xs uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export const PricingInfo: React.FC<PricingInfoProps> = ({
  metadata,
  basePath,
}) => {
  switch (metadata.status) {
    case SalesStatus.AVAILABLE:
      return (
        <div>
          {metadata.price?.amount ? (
            <p className="text-custom-olive text-sm mb-3">
              {metadata.price.amount} {metadata.price.currency || 'KES'}
            </p>
          ) : null}
          <InquiryForm
            artTitle={metadata.title}
            artId={metadata.id}
            basePath={basePath}
          />
        </div>
      );

    case SalesStatus.SOLD:
      return (
        <p className="text-red-400 text-xs uppercase tracking-widest italic">
          Sold
        </p>
      );

    case SalesStatus.NOT_FOR_SALE:
      return (
        <p className="text-gray-400 text-[11px] uppercase tracking-widest">
          NFS
        </p>
      );

    case SalesStatus.INQUIRE_FOR_PRICE:
      return (
        <InquiryForm
          artTitle={metadata.title}
          artId={metadata.id}
          basePath={basePath}
        />
      );

    case SalesStatus.COMMISSION_AVAILABLE:
      return (
        <div>
          <p className="text-custom-olive text-xs uppercase tracking-widest mb-3">
            Commission available
          </p>
          <InquiryForm
            artTitle={metadata.title}
            artId={metadata.id}
            basePath={basePath}
          />
        </div>
      );

    case SalesStatus.DONATED:
      return (
        <p className="text-indigo-400 text-xs uppercase tracking-widest italic">
          Donated for charity
        </p>
      );

    default:
      return null;
  }
};
